import React, { CSSProperties } from 'react';
import { Form, FormProps, FormItemProps, FormInstance } from 'antd';

/**
 * Custom Form wrapper with enhanced styling
 */
export interface MyFormProps extends FormProps {
  formVariant?: 'default' | 'card' | 'inline' | 'compact';
  shadow?: boolean;
  rounded?: boolean;
  padding?: boolean;
}

const MyFormComponent: React.FC<MyFormProps> = ({
  formVariant = 'default',
  shadow = false,
  rounded = false,
  padding = false,
  style,
  className,
  children,
  ...rest
}) => {
  const variantStyles: Record<string, CSSProperties> = {
    default: {},
    card: {
      background: '#fff',
      padding: '24px',
      borderRadius: rounded ? '12px' : '4px',
      boxShadow: shadow ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
    },
    inline: {
      display: 'inline-block',
    },
    compact: {},
  };

  const customStyle: CSSProperties = {
    ...variantStyles[formVariant],
    padding: padding ? '20px' : undefined,
    ...style,
  };

  return (
    <Form style={customStyle} className={className} {...rest}>
      {children as any}
    </Form>
  );
};

/**
 * Custom Form.Item wrapper
 */
export interface MyFormItemProps extends FormItemProps {
  itemVariant?: 'default' | 'filled' | 'bordered';
  compact?: boolean;
}

export const MyFormItem: React.FC<MyFormItemProps> = ({
  itemVariant = 'default',
  compact = false,
  style,
  className,
  children,
  ...rest
}) => {
  const variantStyles: Record<string, CSSProperties> = {
    default: {},
    filled: {
      background: '#f5f5f5',
      padding: '12px',
      borderRadius: '6px',
    },
    bordered: {
      border: '1px solid #d9d9d9',
      padding: '12px',
      borderRadius: '6px',
    },
  };

  const customStyle: CSSProperties = {
    ...variantStyles[itemVariant],
    marginBottom: compact ? '12px' : '24px',
    ...style,
  };

  return (
    <Form.Item style={customStyle} className={className} {...rest}>
      {children}
    </Form.Item>
  );
};

// Create MyForm with static properties
const MyForm = MyFormComponent as typeof MyFormComponent & {
  Item: typeof MyFormItem;
  List: typeof Form.List;
  useForm: typeof Form.useForm;
};

// Attach Form.Item and other static properties
MyForm.Item = MyFormItem;
MyForm.List = Form.List;
MyForm.useForm = Form.useForm;

export default MyForm;
